import React, { useMemo, useState, useContext, createContext } from "react";
import { Link } from "react-router-dom";
import PracticeLayout from "../../components/PracticeLayout";
import PracticeSection from "../../components/PracticeSection";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Copy, KeyRound, ExternalLink, Play, Loader2, Eye } from "lucide-react";
import useDocumentTitle from "../../hooks/useDocumentTitle";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const METHOD_STYLES = {
  GET:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST:   "bg-blue-50 text-blue-700 border-blue-200",
  PUT:    "bg-amber-50 text-amber-800 border-amber-200",
  PATCH:  "bg-amber-50 text-amber-800 border-amber-200",
  DELETE: "bg-red-50 text-red-700 border-red-200",
};

const DEMO_SEEKER = { email: "demo.seeker@qatestology.com", password: "Demo@1234" };
const DEMO_EMPLOYER = { email: "demo.employer@qatestology.com", password: "Demo@1234" };

// Which demo account each endpoint should be tried as (for the copy-paste curl script).
// null means "no auth needed".
const DEMO_ROLE = {
  "auth-register": null,
  "auth-login": null,
  "auth-me": "seeker",
  "profile-get": "seeker",
  "profile-put": "seeker",
  "jobs-list": null,
  "jobs-get": null,
  "jobs-create": "employer",
  "jobs-update": "employer",
  "jobs-delete": "employer", // demo employer is not admin, but the curl still shows the shape
  "apply-post": "seeker",
  "apply-list": "seeker",
};

// Shared state across all endpoint cards on this page.
// Populated automatically after successful "Try it" calls, so learners can
// chain create → update → delete → apply → check without retyping ids.
const IdsContext = createContext({ ids: {}, setId: () => {} });
const useIds = () => useContext(IdsContext);

const ENDPOINTS = [
  {
    group: "Auth",
    id: "auth-register",
    method: "POST",
    path: "/api/auth/register",
    auth: false,
    description: "Create a new account. Response returns a bearer token in the JSON body — use it in an Authorization header.",
    body: `{
  "name": "Test User",
  "email": "test+${Date.now().toString(36)}@example.com",
  "password": "GoodPass1!",
  "role": "job_seeker"
}`,
    uiCheck: { path: "/dashboard", label: "Open Dashboard", hint: "Registration signs you in — dashboard shows the new session." },
  },
  {
    group: "Auth",
    id: "auth-login",
    method: "POST",
    path: "/api/auth/login",
    auth: false,
    description: "Sign in with email + password. Response body includes a bearer `token` you can pass as Authorization header.",
    body: `{
  "email": "demo.seeker@qatestology.com",
  "password": "Demo@1234"
}`,
    uiCheck: { path: "/dashboard", label: "Open Dashboard", hint: "You're now logged in — see your dashboard." },
  },
  {
    group: "Auth",
    id: "auth-me",
    method: "GET",
    path: "/api/auth/me",
    auth: true,
    description: "Return the currently authenticated user.",
    uiCheck: { path: "/dashboard", label: "Open Dashboard", hint: "Same session, rendered as UI." },
  },
  {
    group: "Seeker profile",
    id: "profile-get",
    method: "GET",
    path: "/api/profile/seeker",
    auth: true,
    description: "Fetch the current seeker's profile.",
    uiCheck: { path: "/profile", label: "View profile in UI", hint: "The profile page reads exactly this endpoint." },
  },
  {
    group: "Seeker profile",
    id: "profile-put",
    method: "PUT",
    path: "/api/profile/seeker",
    auth: true,
    description: "Create or update the seeker's profile. Skills must come from GET /api/skills/catalog. Any fake data you send here will appear on the profile page.",
    body: `{
  "headline": "Automation Engineer",
  "location": "Bengaluru",
  "years_experience": 3,
  "skills": ["Selenium WebDriver", "Playwright"],
  "tools": ["Jenkins", "Postman"],
  "phone": "+91 9876543210",
  "linkedin": "https://linkedin.com/in/your-handle",
  "github":   "https://github.com/your-handle"
}`,
    uiCheck: { path: "/profile", label: "View profile in UI", hint: "Refresh the profile page to see the new values you just PUT." },
  },
  {
    group: "Jobs (public)",
    id: "jobs-list",
    method: "GET",
    path: "/api/jobs",
    auth: false,
    description: "List all active jobs, newest first. Optional query: ?q=playwright&skill=k6",
    uiCheck: { path: "/jobs", label: "Open jobs page", hint: "The public jobs list renders this exact response." },
  },
  {
    group: "Jobs (public)",
    id: "jobs-get",
    method: "GET",
    path: "/api/jobs/{job_id}",
    auth: false,
    description: "Fetch a single job by ID. Replace {job_id} in the path (or use one auto-filled from a prior call).",
    uiCheck: { path: "/jobs/{job_id}", label: "Open job in UI", hint: "See the job detail page for this exact id." },
  },
  {
    group: "Employer jobs",
    id: "jobs-create",
    method: "POST",
    path: "/api/jobs",
    auth: true,
    description: "Create a new job posting. Requires role=employer, approved & verified. On success the response's job_id auto-fills into related endpoints below.",
    body: `{
  "title": "Automation QA Engineer",
  "description": "Selenium/Playwright automation for a fintech product.",
  "location": "Remote",
  "job_type": "Full-time",
  "experience_min": 2,
  "experience_max": 5,
  "required_skills": ["Selenium WebDriver", "Java"],
  "nice_to_have": ["Playwright"],
  "salary_currency": "INR",
  "salary_min": 1200000,
  "salary_max": 2000000,
  "salary_period": "yearly",
  "salary_visible": true,
  "salary_range": "₹12L – ₹20L / year"
}`,
    uiCheck: { path: "/jobs/{job_id}", label: "Open new job in UI", hint: "The job page is generated from the doc you just created." },
  },
  {
    group: "Employer jobs",
    id: "jobs-update",
    method: "PUT",
    path: "/api/jobs/{job_id}",
    auth: true,
    description: "Edit a posting (only inside the 7-day window after creation).",
    body: `{
  "title": "Automation QA Engineer (updated)",
  "description": "Updated JD…",
  "location": "Bengaluru",
  "job_type": "Full-time",
  "experience_min": 3,
  "experience_max": 6,
  "required_skills": ["Selenium WebDriver"],
  "nice_to_have": [],
  "salary_currency": "INR",
  "salary_min": 1500000,
  "salary_max": 2500000,
  "salary_period": "yearly",
  "salary_visible": true,
  "salary_range": "₹15L – ₹25L / year"
}`,
    uiCheck: { path: "/jobs/{job_id}", label: "Open updated job in UI", hint: "See the edited title/JD live on the job page." },
  },
  {
    group: "Employer jobs",
    id: "jobs-delete",
    method: "DELETE",
    path: "/api/admin/jobs/{job_id}",
    auth: true,
    description: "Admin-only. Soft-close a job. Pass ?hard=true to purge the job + its applications.",
    uiCheck: { path: "/jobs", label: "Check jobs list", hint: "Closed/purged jobs disappear from the public list." },
  },
  {
    group: "Applications",
    id: "apply-post",
    method: "POST",
    path: "/api/jobs/{job_id}/apply",
    auth: true,
    description: "Seeker applies to a job. Multipart body — optional field `resume` for a per-application resume.",
    uiCheck: { path: "/dashboard", label: "See in My Applications", hint: "The dashboard's applications tab shows the new row." },
  },
  {
    group: "Applications",
    id: "apply-list",
    method: "GET",
    path: "/api/applications/mine",
    auth: true,
    description: "List the current seeker's applications.",
    uiCheck: { path: "/dashboard", label: "See in My Applications", hint: "The dashboard's applications tab renders this endpoint." },
  },
];

function buildCurl({ ep, effectivePath, base }) {
  const url = `${base}${effectivePath}`;
  const role = DEMO_ROLE[ep.id];
  const bodyOneLine = ep.body ? ep.body.replace(/\n/g, "").replace(/\s+/g, " ") : null;

  // Public endpoint — plain curl, nothing to paste.
  if (!ep.auth || role === null) {
    const parts = [`curl -X ${ep.method} '${url}'`];
    if (bodyOneLine) {
      parts[0] += " \\";
      parts.push(`  -H 'Content-Type: application/json' \\`);
      parts.push(`  -d '${bodyOneLine}'`);
    } else {
      parts[0] += " \\";
      parts.push(`  -H 'Accept: application/json'`);
    }
    return parts.join("\n");
  }

  // Auth endpoint — login first, capture token, then call. Copy-paste and it just works.
  const creds = role === "employer" ? DEMO_EMPLOYER : DEMO_SEEKER;
  const loginBody = `{"email":"${creds.email}","password":"${creds.password}"}`;
  const loginLines = [
    `# 1. Log in as the demo ${role} — this returns a bearer token in the body`,
    `TOKEN=$(curl -s -X POST '${base}/api/auth/login' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '${loginBody}' | python3 -c "import sys,json;print(json.load(sys.stdin)['token'])")`,
    ``,
    `# 2. Call the endpoint with the bearer token`,
    `curl -X ${ep.method} '${url}' \\`,
    `  -H "Authorization: Bearer $TOKEN" \\`,
  ];
  if (bodyOneLine) {
    loginLines.push(`  -H 'Content-Type: application/json' \\`);
    loginLines.push(`  -d '${bodyOneLine}'`);
  } else {
    loginLines.push(`  -H 'Accept: application/json'`);
  }
  return loginLines.join("\n");
}

function CurlSnippet({ ep, effectivePath }) {
  const text = buildCurl({ ep, effectivePath, base: BASE });
  return (
    <div className="relative">
      <pre className="tag-mono text-[11px] bg-zinc-950 text-zinc-100 p-3 rounded overflow-x-auto whitespace-pre" data-testid={`curl-${ep.id}`}>{text}</pre>
      <button
        onClick={() => { navigator.clipboard.writeText(text); toast.success("curl copied — paste into a terminal, it just works"); }}
        data-testid={`copy-curl-${ep.id}`}
        className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded bg-zinc-800 text-zinc-300 hover:text-white"
        aria-label="Copy curl"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function EndpointRow({ ep }) {
  const { ids, setId } = useIds();
  const [body, setBody] = useState(ep.body || "");
  const [running, setRunning] = useState(false);
  const [resp, setResp] = useState(null);
  const [success, setSuccess] = useState(false);
  const [jobIdInput, setJobIdInput] = useState("");

  const pathNeedsJobId = ep.path.includes("{job_id}");
  const uiCheckNeedsJobId = (ep.uiCheck?.path || "").includes("{job_id}");
  const currentJobId = (jobIdInput || ids.job_id || "").trim();
  const effectivePath = ep.path.replace("{job_id}", currentJobId || "{job_id}");

  const uiCheckHref = () => {
    if (!ep.uiCheck) return null;
    let p = ep.uiCheck.path;
    if (p.includes("{job_id}")) {
      if (!currentJobId) return null;
      p = p.replace("{job_id}", currentJobId);
    }
    return p;
  };

  const uiHref = uiCheckHref();

  const runIt = async () => {
    setRunning(true); setResp(null); setSuccess(false);
    try {
      let path = ep.path;
      if (path.includes("{job_id}")) {
        if (!currentJobId) {
          toast.error("Enter a job_id first (or run POST /api/jobs to auto-fill one)");
          setRunning(false);
          return;
        }
        path = path.replace("{job_id}", currentJobId);
      }
      const opts = { method: ep.method, credentials: "include", headers: {} };
      if (ep.body) { opts.headers["Content-Type"] = "application/json"; opts.body = body; }
      const r = await fetch(`${BASE}${path}`, opts);
      const text = await r.text();
      let pretty = text; let parsed = null;
      try { parsed = JSON.parse(text); pretty = JSON.stringify(parsed, null, 2); } catch (_) {}
      setResp({ status: r.status, statusText: r.statusText, body: pretty });

      if (r.status >= 200 && r.status < 300) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2500);
        toast.success("Data updated — click 'Check in UI' to see it", { duration: 4000 });

        // Auto-fill shared ids so the next endpoint card is one-click away.
        if (parsed && typeof parsed === "object") {
          if (parsed.job_id) {
            setId("job_id", parsed.job_id);
            setJobIdInput(parsed.job_id);
          }
          if (parsed.application_id) setId("application_id", parsed.application_id);
        }
      } else {
        toast.error(`Request failed (${r.status})`);
      }
    } catch (e) {
      setResp({ status: 0, statusText: "Network error", body: String(e) });
      toast.error("Network error");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card
      className={`p-4 mb-4 transition-shadow duration-500 ${success ? "ring-2 ring-emerald-400 shadow-[0_0_0_6px_rgba(16,185,129,0.15)]" : ""}`}
      data-testid={`endpoint-${ep.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0 flex items-center gap-2">
          <span className={`tag-mono text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${METHOD_STYLES[ep.method]}`}>{ep.method}</span>
          <code className="tag-mono text-sm text-zinc-950 truncate">{ep.path}</code>
          {ep.auth && (
            <span className="inline-flex items-center gap-1 tag-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
              <KeyRound className="h-3 w-3" /> auth
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={runIt} disabled={running} data-testid={`try-${ep.id}`} className="bg-[#002FA7] hover:bg-[#00227A] text-white">
            {running ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Sending…</> : <><Play className="h-3.5 w-3.5 mr-1" /> Try it</>}
          </Button>
          {ep.uiCheck && (
            uiHref ? (
              <a
                href={uiHref}
                target="_blank"
                rel="noreferrer"
                data-testid={`check-ui-${ep.id}`}
                title={ep.uiCheck.hint || ""}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm font-medium hover:bg-emerald-100 hover:border-emerald-400 transition-colors"
              >
                <Eye className="h-3.5 w-3.5" /> {ep.uiCheck.label || "Check in UI"}
              </a>
            ) : (
              <button
                type="button"
                disabled
                data-testid={`check-ui-${ep.id}`}
                title={pathNeedsJobId ? "Enter a job_id above first (or run POST /api/jobs to auto-fill)" : "Run \"Try it\" first — the returned id enables this link"}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-400 text-sm font-medium cursor-not-allowed"
              >
                <Eye className="h-3.5 w-3.5" /> {ep.uiCheck.label || "Check in UI"}
              </button>
            )
          )}
        </div>
      </div>
      <p className="text-sm text-zinc-700 mb-3">{ep.description}</p>

      {pathNeedsJobId && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <label htmlFor={`jobid-${ep.id}`} className="text-[10px] tag-mono uppercase tracking-widest text-zinc-500 font-bold">job_id</label>
          <Input
            id={`jobid-${ep.id}`}
            value={jobIdInput || ids.job_id || ""}
            onChange={(e) => setJobIdInput(e.target.value)}
            placeholder="job_xxxxxxxx  (or run POST /api/jobs first)"
            className="tag-mono text-xs h-8 max-w-md"
            data-testid={`jobid-${ep.id}`}
          />
          {ids.job_id && (
            <span className="tag-mono text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
              auto: {ids.job_id}
            </span>
          )}
        </div>
      )}

      {ep.body && (
        <div className="mb-3">
          <div className="text-[10px] tag-mono uppercase tracking-widest text-zinc-500 font-bold mb-1">request body (JSON)</div>
          <Textarea rows={Math.min(10, body.split("\n").length + 1)} value={body} onChange={(e) => setBody(e.target.value)} data-testid={`body-${ep.id}`} className="tag-mono text-xs" />
        </div>
      )}

      <div>
        <div className="text-[10px] tag-mono uppercase tracking-widest text-zinc-500 font-bold mb-1">curl example</div>
        <CurlSnippet ep={ep} effectivePath={effectivePath} />
      </div>

      {ep.uiCheck?.hint && (
        <p className="mt-2 text-[11px] text-zinc-500">
          <Eye className="inline h-3 w-3 mr-1 -mt-0.5" />
          {ep.uiCheck.hint}
        </p>
      )}

      {resp && (
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-1">
            <span className={`tag-mono text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${
              resp.status === 0 ? "bg-red-50 text-red-700 border-red-200"
              : resp.status < 300 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : resp.status < 500 ? "bg-amber-50 text-amber-800 border-amber-200"
              : "bg-red-50 text-red-700 border-red-200"
            }`}>{resp.status} {resp.statusText}</span>
          </div>
          <pre className="tag-mono text-[11px] bg-zinc-50 border border-zinc-200 p-3 rounded overflow-x-auto whitespace-pre max-h-64" data-testid={`response-${ep.id}`}>{resp.body}</pre>
        </div>
      )}
    </Card>
  );
}

export default function ApiPlayground() {
  useDocumentTitle("API Playground — Practice");
  const [ids, setIds] = useState({ job_id: "", application_id: "" });
  const setId = (key, val) => setIds((prev) => ({ ...prev, [key]: val }));

  const grouped = useMemo(() => {
    const m = {};
    ENDPOINTS.forEach((e) => { (m[e.group] = m[e.group] || []).push(e); });
    return m;
  }, []);
  return (
    <IdsContext.Provider value={{ ids, setId }}>
      <PracticeLayout
        eyebrow="API Testing Playground"
        title="A real REST API to practise against"
        subtitle={`Base URL: ${BASE || "your backend URL"} — everything below is live. Read endpoints are open; write endpoints authenticate with a bearer token (each curl below fetches its own — nothing to paste from your browser).`}
        seo={{
          title: "API Testing Practice — Real CRUD sandbox",
          description: "Practise API testing against a real REST API — CRUD endpoints for auth, profile, jobs and applications with copy-paste curl and Try-it buttons.",
          keywords: "api testing website, api testing practice, rest api sandbox, crud api demo, postman practice",
          image: "/og/og-api.svg",
        }}
      >
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200" data-testid="api-auth-note">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 rounded-md bg-white border border-amber-200 grid place-items-center text-amber-700 flex-shrink-0">
              <KeyRound className="h-5 w-5" />
            </span>
            <div className="text-sm text-zinc-800">
              <div className="font-semibold text-zinc-950">How auth works here — copy, paste, run</div>
              <p className="mt-0.5">
                Every auth-required endpoint shows a <b>two-step curl</b>: log in as a demo account → capture the <code className="tag-mono text-xs bg-white px-1 rounded border">token</code> from the JSON response → call the endpoint with <code className="tag-mono text-xs bg-white px-1 rounded border">Authorization: Bearer $TOKEN</code>. Copy → paste in a terminal → it just works. Nothing to grab from your browser.
              </p>
              <p className="mt-1 text-zinc-700">
                Demo accounts (also pre-filled on the <Link to="/login" className="underline text-[#002FA7]">Sign in</Link> page):
                {" "}<code className="tag-mono text-xs bg-white px-1 rounded border">demo.seeker@qatestology.com</code>
                {" · "}<code className="tag-mono text-xs bg-white px-1 rounded border">demo.employer@qatestology.com</code>
                {" · pw "}<code className="tag-mono text-xs bg-white px-1 rounded border">Demo@1234</code>
              </p>
              <p className="mt-2 text-zinc-800">
                <b className="text-zinc-950">Tip:</b> click <span className="inline-flex items-center gap-1 text-emerald-800"><Eye className="h-3 w-3" /> Check in UI</span> after <b>Try it</b> to jump to the page where the data change shows up. For bearer-token deep-dive, see Level 7's{" "}
                <Link to="/practice/level/advanced" className="underline text-[#002FA7]">Authentication</Link> section.
              </p>
              <div className="mt-2 flex gap-3">
                <a href={`${BASE}/docs`} target="_blank" rel="noreferrer" data-testid="link-swagger" className="tag-mono text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  Swagger UI (/docs) <ExternalLink className="h-3 w-3" />
                </a>
                <a href={`${BASE}/redoc`} target="_blank" rel="noreferrer" data-testid="link-redoc" className="tag-mono text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  ReDoc (/redoc) <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </Card>

        {Object.entries(grouped).map(([group, list]) => (
          <PracticeSection key={group} id={`group-${group.replace(/\s+/g, "-").toLowerCase()}`} title={group}>
            {list.map((ep) => <EndpointRow key={ep.id} ep={ep} />)}
          </PracticeSection>
        ))}

        <div className="mt-6 tag-mono text-[11px] text-zinc-500">
          Tip · every response returns real data — feel free to spin up bots against this endpoint suite as long as you don't spam other users.
        </div>
      </PracticeLayout>
    </IdsContext.Provider>
  );
}
